import React, { useEffect, useState, useRef, Component } from 'react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { DownloadIcon, ExternalLinkIcon, ChevronDownIcon } from 'lucide-react';
import { ComparisonType } from '../dashboard/ComparisonSelector';
import { AcceptanceFilters } from './AcceptanceFilters';
import { KeyMetricsSection } from './KeyMetricsSection';
import { BreakdownTable } from './BreakdownTable';
import { DateRangePicker } from '../dashboard/DateRangePicker';
import { GranularitySelector, Granularity } from '../dashboard/GranularitySelector';
import { MetricChart } from '../dashboard/MetricChart';
import { DownloadReportModal } from './DownloadReportModal';
interface DateRange {
  start: Date;
  end: Date;
}
interface AcceptanceTabProps {
  dateRange: DateRange;
  granularity: string;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
type KeyMetric = 'success-rate' | 'accepted-volume' | 'accepted-payments';
export function AcceptanceTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: AcceptanceTabProps) {
  const [selectedMetric, setSelectedMetric] = useState<KeyMetric>('success-rate');
  const [chartData, setChartData] = useState<any[]>([]);
  const [previousChartData, setPreviousChartData] = useState<any[]>([]);
  const [comparison, setComparison] = useState<ComparisonType>('previous_period');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  // Filter states
  const [deduplication, setDeduplication] = useState<'deduplicated' | 'raw'>('deduplicated');
  const [successRateMetric, setSuccessRateMetric] = useState<'payment-success-rate' | 'authorization-rate'>('payment-success-rate');
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Generate mock data based on date range
  useEffect(() => {
    const data = generateChartData(dateRange, selectedMetric);
    setChartData(data.current);
    setPreviousChartData(data.previous);
  }, [dateRange, selectedMetric]);
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const formatYAxis = (value: any) => {
    if (selectedMetric === 'success-rate') {
      return `${value}%`;
    } else if (selectedMetric === 'accepted-volume') {
      if (value >= 1000) {
        return `MYR ${(value / 1000).toFixed(0)}K`;
      }
      return `MYR ${value}`;
    } else {
      return value.toString();
    }
  };
  // Get Y-axis domain based on metric type
  const getYAxisDomain = () => {
    if (selectedMetric === 'success-rate') {
      return [0, 100];
    }
    return undefined; // Auto-scale for other metrics
  };
  // Define metrics for KeyMetricsSection - title changes based on successRateMetric filter
  const getMetricTitle = () => {
    return successRateMetric === 'payment-success-rate' ? 'Payment success rate' : 'Authorization rate';
  };
  const getMetricTooltip = () => {
    return successRateMetric === 'payment-success-rate' ? "Percentage of payments approved by payment method issuers, out of all attempted payments. Includes blocked payments, and payments that didn't complete 3D Secure authentication." : "Percentage of payments approved by payment method issuers, out of all attempted payments. Excludes blocked payments, and payments that didn't complete 3D Secure authentication.";
  };
  const metrics: Array<{
    id: KeyMetric;
    title: string;
    value: string;
    change: string;
    tooltipText: string;
  }> = [{
    id: 'success-rate',
    title: successRateMetric === 'payment-success-rate' ? 'Payment success rate' : 'Authorization rate',
    value: '98.00%',
    change: '-0.97%',
    tooltipText: successRateMetric === 'payment-success-rate' ? "Percentage of payments approved by payment method issuers, out of all attempted payments. Includes blocked payments, and payments that didn't complete 3D Secure authentication." : "Percentage of payments approved by payment method issuers, out of all attempted payments. Excludes blocked payments, and payments that didn't complete 3D Secure authentication."
  }, {
    id: 'accepted-volume',
    title: 'Accepted volume',
    value: 'MYR 3.21K',
    change: '-33.88%',
    tooltipText: 'Total amount of accepted payments. Defaults to your settlement currency.'
  }, {
    id: 'accepted-payments',
    title: 'Accepted payments',
    value: '49',
    change: '-48.42%',
    tooltipText: 'Total number of payments approved by payment method issuers.'
  }];
  // Get data key based on selected metric
  const getDataKey = () => {
    if (selectedMetric === 'success-rate') return 'rate';
    if (selectedMetric === 'accepted-volume') return 'amount';
    return 'count';
  };
  // Breakdown table data
  const breakdownRows = [{
    category: 'Accepted payments',
    volume: 'MYR 3,214.62',
    share: '98.59%',
    change: '-33.88%'
  }, {
    category: '✓ Accepted',
    volume: 'MYR 3,214.62',
    share: '98.59%',
    change: '-33.88%',
    isSubItem: true
  }, {
    category: 'Failed payments',
    volume: 'MYR 45.85',
    share: '1.41%',
    change: '+85.63%'
  }, {
    category: 'Issuer declined',
    volume: 'MYR 45.85',
    share: '1.41%',
    change: '+85.63%',
    isSubItem: true
  }, {
    category: '3DS failures',
    volume: 'MYR 0.00',
    share: '0.00%',
    change: '+0.00%',
    isSubItem: true
  }, {
    category: 'Blocked',
    volume: 'MYR 0.00',
    share: '0.00%',
    change: '+0.00%',
    isSubItem: true
  }, {
    category: 'Total',
    volume: 'MYR 3,260.47',
    share: '100.00%',
    change: '',
    isTotal: true
  }];
  return <div className="space-y-6" data-id="element-383">
      {/* Main Filters Row - Larger buttons */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-384">
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-385" />
        <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-386" />
        <AcceptanceFilters deduplication={deduplication} onDeduplicationChange={setDeduplication} successRateMetric={successRateMetric} onSuccessRateMetricChange={setSuccessRateMetric} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} interactionTypes={interactionTypes} onInteractionTypesChange={setInteractionTypes} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-387" />
      </div>

      {/* Key Metrics Section with Chart */}
      <KeyMetricsSection title="Key metrics" metrics={metrics} selectedMetricId={selectedMetric} onMetricSelect={id => setSelectedMetric(id as KeyMetric)} chartData={chartData} previousChartData={previousChartData} dataKey={getDataKey()} yAxisFormatter={formatYAxis} tooltipValueFormatter={value => formatYAxis(value)} dateFormatter={formatDate} updateTime="Updated yesterday" chartColor="#D4AF37" comparison={comparison} onComparisonChange={setComparison} dateRange={dateRange} yAxisDomain={getYAxisDomain()} onDownload={() => setShowDownloadModal(true)} data-id="element-388" />

      {/* Breakdown Table - only show for accepted-volume */}
      {selectedMetric === 'accepted-volume' && <BreakdownTable rows={breakdownRows} data-id="element-389" />}

      {/* Card Payments Breakdown */}
      <CardPaymentsBreakdown dateRange={dateRange} onDownload={() => setShowDownloadModal(true)} successRateMetric={successRateMetric} data-id="element-390" />

      {/* Failed Card Payments */}
      <FailedCardPayments dateRange={dateRange} onDownload={() => setShowDownloadModal(true)} data-id="element-391" />

      {/* Blocked Card Payments */}
      <BlockedCardPayments data-id="element-392" />

      {/* Download Modal */}
      <DownloadReportModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} dateRange={dateRange} deduplication={deduplication} data-id="element-393" />
    </div>;
}
// Card Payments Breakdown Component
type BreakdownView = 'payment-volume' | 'payments' | 'share-of-payments' | 'authorization-rate';
type PivotOption = '3DS outcome' | 'Card brand' | 'Card country' | 'Card type' | 'Currency' | 'CVC response' | 'Input method' | 'Interaction type' | 'Network token status' | 'Postal code response' | 'Retry status';
function CardPaymentsBreakdown({
  dateRange,
  onDownload,
  successRateMetric
}: {
  dateRange: DateRange;
  onDownload: () => void;
  successRateMetric: 'payment-success-rate' | 'authorization-rate';
}) {
  const [selectedView, setSelectedView] = useState<BreakdownView>('payment-volume');
  const [pivotBy, setPivotBy] = useState<PivotOption>('Card type');
  const [showPivotDropdown, setShowPivotDropdown] = useState(false);
  const pivotRef = useRef<HTMLDivElement>(null);
  const pivotOptions: PivotOption[] = ['3DS outcome', 'Card brand', 'Card country', 'Card type', 'Currency', 'CVC response', 'Input method', 'Interaction type', 'Network token status', 'Postal code response', 'Retry status'];
  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pivotRef.current && !pivotRef.current.contains(event.target as Node)) {
        setShowPivotDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const chartData = generateCardBreakdownData(dateRange, selectedView);
  const getChartTitle = () => {
    switch (selectedView) {
      case 'payment-volume':
        return 'Payment volume';
      case 'payments':
        return 'Payments';
      case 'share-of-payments':
        return 'Share of payments';
      case 'authorization-rate':
        return 'Authorization rate';
    }
  };
  const getChartValue = () => {
    switch (selectedView) {
      case 'payment-volume':
        return 'MYR 3,260.80';
      case 'payments':
        return '50';
      case 'share-of-payments':
        return '100.00%';
      case 'authorization-rate':
        return '98.00%';
    }
  };
  const formatYAxis = (value: any) => {
    switch (selectedView) {
      case 'payment-volume':
        // Add decimal for better readability when values are small
        if (value >= 1000) {
          return `MYR ${(value / 1000).toFixed(1)}K`;
        }
        return `MYR ${value}`;
      case 'payments':
        return value.toString();
      case 'share-of-payments':
        return `${value}%`;
      case 'authorization-rate':
        return `${value}%`;
    }
  };
  const getYAxisDomain = () => {
    if (selectedView === 'share-of-payments' || selectedView === 'authorization-rate') {
      return [0, 100];
    }
    return undefined;
  };
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  // Custom tooltip formatter for proper labels
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3" data-id="element-394">
          <p className="text-sm font-medium text-gray-900 mb-2" data-id="element-395">{label}</p>
          {payload.map((entry: any, index: number) => {
          const labelMap: {
            [key: string]: string;
          } = {
            creditCard: 'Credit card',
            debitCard: 'Debit card',
            incorrectNumber: 'Incorrect number'
          };
          const displayLabel = labelMap[entry.dataKey] || entry.dataKey;
          const formattedValue = selectedView === 'payment-volume' || selectedView === 'failed-volume' ? `MYR ${entry.value.toFixed(2)}` : entry.value;
          return <p key={index} className="text-sm text-gray-700" data-id="element-396">
                {displayLabel} : {formattedValue}
              </p>;
        })}
        </div>;
    }
    return null;
  };
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-397">
      <div className="flex items-center justify-between mb-4" data-id="element-398">
        <div data-id="element-399">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-400">
            Card payments breakdown
          </h3>
          <p className="text-sm text-gray-600 mt-1" data-id="element-401">
            Analyze card authorization success, and the impact on payment
            volume. Includes issuer declined payments.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-402">
          <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-403">
            <DownloadIcon size={14} data-id="element-404" />
            Download
          </button>
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-405">
            <ExternalLinkIcon size={14} data-id="element-406" />
            Explore
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6" data-id="element-407">
        <div className="flex items-center gap-2" data-id="element-408">
          <span className="text-sm text-gray-700" data-id="element-409">Pivot by</span>
          <div className="relative" ref={pivotRef} data-id="element-410">
            <button onClick={() => setShowPivotDropdown(!showPivotDropdown)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-411">
              <span data-id="element-412">{pivotBy}</span>
              <ChevronDownIcon size={16} data-id="element-413" />
            </button>

            {showPivotDropdown && <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 max-h-80 overflow-y-auto" data-id="element-414">
                {pivotOptions.map(option => <button key={option} onClick={() => {
              setPivotBy(option);
              setShowPivotDropdown(false);
            }} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${pivotBy === option ? 'text-gray-900 font-medium' : 'text-gray-700'}`} data-id="element-415">
                    <span data-id="element-416">{option}</span>
                    {pivotBy === option && <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-blue-600" data-id="element-417">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-418" />
                      </svg>}
                  </button>)}
              </div>}
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-419">
          <button onClick={() => setSelectedView('payment-volume')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'payment-volume' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-420">
            Payment volume
          </button>
          <button onClick={() => setSelectedView('payments')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'payments' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-421">
            Payments
          </button>
          <button onClick={() => setSelectedView('share-of-payments')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'share-of-payments' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-422">
            Share of payments
          </button>
          <button onClick={() => setSelectedView('authorization-rate')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'authorization-rate' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-423">
            {successRateMetric === 'payment-success-rate' ? 'Payment success rate' : 'Authorization rate'}
          </button>
        </div>
      </div>

      {/* Use MetricChart for Authorization rate, custom charts for others */}
      {selectedView === 'authorization-rate' ? <MetricChart title={getChartTitle()} currentValue={getChartValue()} growthPercent={-0.97} previousValue="98.00%" data={chartData.current} previousData={chartData.previous} dataKey="value" yAxisFormatter={formatYAxis} tooltipValueFormatter={formatYAxis} dateFormatter={formatDate} updateTime="Updated yesterday" chartColor="#D4AF37" data-id="element-424" /> : <>
          <div className="mb-4" data-id="element-425">
            <div className="text-sm text-gray-600 mb-1 flex items-center gap-1" data-id="element-426">
              {getChartTitle()}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-400" data-id="element-427">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" data-id="element-428" />
                <path d="M7 4.5V7M7 9.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-429" />
              </svg>
            </div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-430">
              {getChartValue()}
            </div>
          </div>

          {/* Chart - Different types based on selected view */}
          <div className="h-64 mb-6" data-id="element-431">
            <ResponsiveContainer width="100%" height="100%" data-id="element-432">
              {selectedView === 'share-of-payments' ? <BarChart data={chartData.current} margin={{
            left: 0,
            right: 0,
            top: 5,
            bottom: 20
          }} data-id="element-433">
                  <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e5e7eb" data-id="element-434" />
                  <XAxis dataKey="date" tick={{
              fill: '#666',
              fontSize: 11
            }} data-id="element-435" />
                  <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={60} tick={{
              fill: '#666',
              fontSize: 12
            }} domain={getYAxisDomain()} data-id="element-436" />
                  <Tooltip content={<CustomTooltip data-id="element-438" />} data-id="element-437" />
                  <Bar dataKey="creditCard" stackId="a" fill="#4f46e5" radius={[0, 0, 0, 0]} data-id="element-439" />
                  <Bar dataKey="debitCard" stackId="a" fill="#a78bfa" radius={[2, 2, 0, 0]} data-id="element-440" />
                </BarChart> : <BarChart data={chartData.current} margin={{
            left: 0,
            right: 0,
            top: 5,
            bottom: 20
          }} data-id="element-441">
                  <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e5e7eb" data-id="element-442" />
                  <XAxis dataKey="date" tick={{
              fill: '#666',
              fontSize: 11
            }} data-id="element-443" />
                  <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={60} tick={{
              fill: '#666',
              fontSize: 12
            }} domain={getYAxisDomain()} data-id="element-444" />
                  <Tooltip content={<CustomTooltip data-id="element-446" />} data-id="element-445" />
                  {/* Stacked bars: Credit card (dark blue) + Debit card (light purple) */}
                  <Bar dataKey="creditCard" stackId="a" fill="#4f46e5" radius={[0, 0, 0, 0]} data-id="element-447" />
                  <Bar dataKey="debitCard" stackId="a" fill="#a78bfa" radius={[2, 2, 0, 0]} data-id="element-448" />
                </BarChart>}
            </ResponsiveContainer>
          </div>

          <div className="text-xs text-gray-500 mb-4" data-id="element-449">Updated yesterday</div>
        </>}

      {/* Table */}
      <table className="w-full" data-id="element-450">
        <thead data-id="element-451">
          <tr className="border-b border-gray-200" data-id="element-452">
            <th className="text-left text-sm font-medium text-gray-700 pb-3" data-id="element-453">
              Card type
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-454">
              Payment volume
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-455">
              Payments
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-456">
              Share of payments
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-457">
              Success rate
            </th>
          </tr>
        </thead>
        <tbody data-id="element-458">
          <tr className="border-b border-gray-200" data-id="element-459">
            <td className="py-3 text-sm text-gray-900 flex items-center gap-2" data-id="element-460">
              <div className="relative w-4 h-4 flex items-center justify-center bg-indigo-600 rounded cursor-pointer" data-id="element-461">
                <input type="checkbox" className="absolute inset-0 opacity-0 cursor-pointer" checked readOnly data-id="element-462" />
                <svg className="w-3 h-3 text-white pointer-events-none" viewBox="0 0 12 12" fill="none" data-id="element-463">
                  <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-464" />
                </svg>
              </div>
              Credit card
            </td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-465">
              MYR 3,178.83
            </td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-466">44</td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-467">88.00%</td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-468">97.73%</td>
          </tr>
          <tr className="border-b border-gray-200" data-id="element-469">
            <td className="py-3 text-sm text-gray-900 flex items-center gap-2" data-id="element-470">
              <div className="relative w-4 h-4 flex items-center justify-center bg-purple-400 rounded cursor-pointer" data-id="element-471">
                <input type="checkbox" className="absolute inset-0 opacity-0 cursor-pointer" checked readOnly data-id="element-472" />
                <svg className="w-3 h-3 text-white pointer-events-none" viewBox="0 0 12 12" fill="none" data-id="element-473">
                  <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-474" />
                </svg>
              </div>
              Debit card
            </td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-475">MYR 81.97</td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-476">6</td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-477">12.00%</td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-478">100.00%</td>
          </tr>
          <tr className="border-t-2 border-gray-300" data-id="element-479">
            <td className="py-3 text-sm font-semibold text-gray-900" data-id="element-480">Total</td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-481">
              MYR 3,260.80
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-482">
              50
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-483">
              100.00%
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-484">
              98.00%
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
}
// Failed Card Payments Component
type FailedView = 'failed-volume' | 'failed' | 'share-of-failures' | 'failure-rate';
function FailedCardPayments({
  dateRange,
  onDownload
}: {
  dateRange: DateRange;
  onDownload: () => void;
}) {
  const [selectedView, setSelectedView] = useState<FailedView>('failed-volume');
  const chartData = generateFailedPaymentsData(dateRange, selectedView);
  const getChartTitle = () => {
    switch (selectedView) {
      case 'failed-volume':
        return 'Failed payment volume';
      case 'failed':
        return 'Failed';
      case 'share-of-failures':
        return 'Share of failures';
      case 'failure-rate':
        return 'Failure rate';
    }
  };
  const getChartValue = () => {
    switch (selectedView) {
      case 'failed-volume':
        return 'MYR 45.85';
      case 'failed':
        return '1';
      case 'share-of-failures':
        return '100.00%';
      case 'failure-rate':
        return '2.00%';
    }
  };
  const formatYAxis = (value: any) => {
    switch (selectedView) {
      case 'failed-volume':
        if (value >= 1000) {
          return `MYR ${(value / 1000).toFixed(1)}K`;
        }
        return `MYR ${value}`;
      case 'failed':
        return value.toString();
      case 'share-of-failures':
        return `${value}%`;
      case 'failure-rate':
        return `${value}%`;
    }
  };
  const getYAxisDomain = () => {
    if (selectedView === 'share-of-failures' || selectedView === 'failure-rate') {
      return [0, 100];
    }
    return undefined;
  };
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  // Custom tooltip formatter for proper labels
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3" data-id="element-485">
          <p className="text-sm font-medium text-gray-900 mb-2" data-id="element-486">{label}</p>
          {payload.map((entry: any, index: number) => {
          const labelMap: {
            [key: string]: string;
          } = {
            incorrectNumber: 'Incorrect number'
          };
          const displayLabel = labelMap[entry.dataKey] || entry.dataKey;
          const formattedValue = selectedView === 'failed-volume' ? `MYR ${entry.value.toFixed(2)}` : entry.value;
          return <p key={index} className="text-sm text-gray-700" data-id="element-487">
                {displayLabel} : {formattedValue}
              </p>;
        })}
        </div>;
    }
    return null;
  };
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-488">
      <div className="flex items-center justify-between mb-4" data-id="element-489">
        <div data-id="element-490">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-491">
            Failed card payments
          </h3>
          <p className="text-sm text-gray-600 mt-1" data-id="element-492">
            Find out why card payments failed.{' '}
            <a href="#" className="text-blue-600 hover:underline" data-id="element-493">
              Learn more about decline codes
            </a>
            .
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-494">
          <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-495">
            <DownloadIcon size={14} data-id="element-496" />
            Download
          </button>
          <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-497">
            <ExternalLinkIcon size={14} data-id="element-498" />
            Explore
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6" data-id="element-499">
        <button onClick={() => setSelectedView('failed-volume')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'failed-volume' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-500">
          Failed volume
        </button>
        <button onClick={() => setSelectedView('failed')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'failed' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-501">
          Failed
        </button>
        <button onClick={() => setSelectedView('share-of-failures')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'share-of-failures' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-502">
          Share of failures
        </button>
        <button onClick={() => setSelectedView('failure-rate')} className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${selectedView === 'failure-rate' ? 'text-blue-600 bg-blue-50 border-blue-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`} data-id="element-503">
          Failure rate
        </button>
      </div>

      {/* Use MetricChart for Failure rate, custom charts for others */}
      {selectedView === 'failure-rate' ? <MetricChart title={getChartTitle()} currentValue={getChartValue()} growthPercent={0} previousValue="2.00%" data={chartData.current} previousData={chartData.previous} dataKey="value" yAxisFormatter={formatYAxis} tooltipValueFormatter={formatYAxis} dateFormatter={formatDate} updateTime="Updated yesterday" chartColor="#D4AF37" data-id="element-504" /> : <>
          <div className="mb-4" data-id="element-505">
            <div className="text-sm text-gray-600 mb-1 flex items-center gap-1" data-id="element-506">
              {getChartTitle()}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-gray-400" data-id="element-507">
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" data-id="element-508" />
                <path d="M7 4.5V7M7 9.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-509" />
              </svg>
            </div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-510">
              {getChartValue()}
            </div>
          </div>

          {/* Chart - Different types based on selected view */}
          <div className="h-64 mb-6" data-id="element-511">
            <ResponsiveContainer width="100%" height="100%" data-id="element-512">
              {selectedView === 'share-of-failures' ? <BarChart data={chartData.current} margin={{
            left: 0,
            right: 0,
            top: 5,
            bottom: 20
          }} data-id="element-513">
                  <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e5e7eb" data-id="element-514" />
                  <XAxis dataKey="date" tick={{
              fill: '#666',
              fontSize: 11
            }} data-id="element-515" />
                  <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={60} tick={{
              fill: '#666',
              fontSize: 12
            }} domain={getYAxisDomain()} data-id="element-516" />
                  <Tooltip content={<CustomTooltip data-id="element-518" />} data-id="element-517" />
                  <Bar dataKey="incorrectNumber" stackId="a" fill="#4f46e5" radius={[2, 2, 0, 0]} data-id="element-519" />
                </BarChart> : <BarChart data={chartData.current} margin={{
            left: 0,
            right: 0,
            top: 5,
            bottom: 20
          }} data-id="element-520">
                  <CartesianGrid strokeDasharray="0" vertical={false} stroke="#e5e7eb" data-id="element-521" />
                  <XAxis dataKey="date" tick={{
              fill: '#666',
              fontSize: 11
            }} data-id="element-522" />
                  <YAxis tickFormatter={formatYAxis} axisLine={false} tickLine={false} width={60} tick={{
              fill: '#666',
              fontSize: 12
            }} domain={getYAxisDomain()} data-id="element-523" />
                  <Tooltip content={<CustomTooltip data-id="element-525" />} data-id="element-524" />
                  <Bar dataKey="incorrectNumber" stackId="a" fill="#4f46e5" radius={[2, 2, 0, 0]} data-id="element-526" />
                </BarChart>}
            </ResponsiveContainer>
          </div>

          <div className="text-xs text-gray-500 mb-4" data-id="element-527">Updated yesterday</div>
        </>}

      {/* Table */}
      <table className="w-full" data-id="element-528">
        <thead data-id="element-529">
          <tr className="border-b border-gray-200" data-id="element-530">
            <th className="text-left text-sm font-medium text-gray-700 pb-3" data-id="element-531">
              Reason
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-532">
              Failed volume
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-533">
              Failed count
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-534">
              Share of failures
            </th>
            <th className="text-right text-sm font-medium text-gray-700 pb-3" data-id="element-535">
              Failure rate
            </th>
          </tr>
        </thead>
        <tbody data-id="element-536">
          <tr className="border-b border-gray-200" data-id="element-537">
            <td className="py-3 text-sm text-gray-900 flex items-center gap-2" data-id="element-538">
              <div className="relative w-4 h-4 flex items-center justify-center bg-indigo-600 rounded cursor-pointer" data-id="element-539">
                <input type="checkbox" className="absolute inset-0 opacity-0 cursor-pointer" checked readOnly data-id="element-540" />
                <svg className="w-3 h-3 text-white pointer-events-none" viewBox="0 0 12 12" fill="none" data-id="element-541">
                  <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-542" />
                </svg>
              </div>
              Incorrect number
            </td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-543">MYR 45.85</td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-544">1</td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-545">100.00%</td>
            <td className="py-3 text-sm text-right text-gray-900" data-id="element-546">2.00%</td>
          </tr>
          <tr className="border-t-2 border-gray-300" data-id="element-547">
            <td className="py-3 text-sm font-semibold text-gray-900" data-id="element-548">Total</td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-549">
              MYR 45.85
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-550">
              1
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-551">
              100.00%
            </td>
            <td className="py-3 text-sm text-right font-semibold text-gray-900" data-id="element-552">
              2.00%
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
}
// Blocked Card Payments Component
function BlockedCardPayments() {
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-553">
      <div className="flex items-center justify-between mb-4" data-id="element-554">
        <div data-id="element-555">
          <h3 className="text-sm font-medium text-gray-900" data-id="element-556">
            Blocked card payments
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-557">
            Find out why some card payments were blocked.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center py-12" data-id="element-558">
        <div className="text-center" data-id="element-559">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" data-id="element-560">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-561">
              <circle cx="32" cy="32" r="30" stroke="#e5e7eb" strokeWidth="2" data-id="element-562" />
              <path d="M32 20v16M32 44h.01" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" data-id="element-563" />
            </svg>
          </div>
          <p className="text-sm text-gray-600" data-id="element-564">
            No payments matching this selection.
          </p>
          <button className="text-sm text-blue-600 hover:underline mt-2" data-id="element-565">
            Clear filters or select another date range and try again.
          </button>
        </div>
      </div>
    </div>;
}
// Data Generation Functions
function generateChartData(dateRange: DateRange, metric: KeyMetric) {
  const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
  const dataPoints = Math.min(days, 12);
  const current = [];
  const previous = [];
  for (let i = 0; i < dataPoints; i++) {
    // Current period dates
    const currentDate = new Date(dateRange.start);
    currentDate.setDate(currentDate.getDate() + Math.floor(i / dataPoints * days));
    // Previous period dates - actual historical dates (current date - days in range)
    const previousDate = new Date(currentDate);
    previousDate.setDate(previousDate.getDate() - days);
    let value;
    if (metric === 'success-rate') {
      // Success rate should be between 85-100%
      value = 85 + Math.random() * 15;
    } else if (metric === 'accepted-volume') {
      value = 200 + Math.random() * 400;
    } else {
      value = 5 + Math.floor(Math.random() * 10);
    }
    current.push({
      date: currentDate.toISOString().split('T')[0],
      rate: metric === 'success-rate' ? parseFloat(value.toFixed(2)) : undefined,
      amount: metric === 'accepted-volume' ? parseFloat(value.toFixed(2)) : undefined,
      count: metric === 'accepted-payments' ? Math.floor(value) : undefined
    });
    // Previous period uses ACTUAL historical date
    previous.push({
      date: previousDate.toISOString().split('T')[0],
      rate: metric === 'success-rate' ? parseFloat((85 + Math.random() * 15).toFixed(2)) : undefined,
      amount: metric === 'accepted-volume' ? parseFloat((value * (0.9 + Math.random() * 0.2)).toFixed(2)) : undefined,
      count: metric === 'accepted-payments' ? Math.floor(value * (0.9 + Math.random() * 0.2)) : undefined
    });
  }
  return {
    current,
    previous
  };
}
function generateCardBreakdownData(dateRange: DateRange, view: BreakdownView) {
  const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
  const dataPoints = Math.min(days, 12);
  const current = [];
  const previous = [];
  for (let i = 0; i < dataPoints; i++) {
    const date = new Date(dateRange.start);
    date.setDate(date.getDate() + Math.floor(i / dataPoints * days));
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    if (view === 'payment-volume' || view === 'payments') {
      // Stacked data: Credit card + Debit card
      const creditCardValue = view === 'payment-volume' ? parseFloat((250 + Math.random() * 350).toFixed(2)) // 2 decimal points
      : 6 + Math.floor(Math.random() * 6);
      const debitCardValue = view === 'payment-volume' ? parseFloat((50 + Math.random() * 100).toFixed(2)) // 2 decimal points
      : 1 + Math.floor(Math.random() * 3);
      current.push({
        date: dateStr,
        creditCard: creditCardValue,
        debitCard: debitCardValue
      });
    } else if (view === 'share-of-payments') {
      const creditCard = 70 + Math.random() * 20;
      current.push({
        date: dateStr,
        creditCard: parseFloat(creditCard.toFixed(2)),
        debitCard: parseFloat((100 - creditCard).toFixed(2))
      });
    } else if (view === 'authorization-rate') {
      // Previous period uses actual historical date
      const previousDate = new Date(date);
      previousDate.setDate(previousDate.getDate() - days);
      current.push({
        date: date.toISOString().split('T')[0],
        value: parseFloat((85 + Math.random() * 15).toFixed(2))
      });
      previous.push({
        date: previousDate.toISOString().split('T')[0],
        value: parseFloat((85 + Math.random() * 15).toFixed(2))
      });
    }
  }
  return {
    current,
    previous
  };
}
function generateFailedPaymentsData(dateRange: DateRange, view: FailedView) {
  const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
  const dataPoints = Math.min(days, 12);
  const current = [];
  const previous = [];
  for (let i = 0; i < dataPoints; i++) {
    const date = new Date(dateRange.start);
    date.setDate(date.getDate() + Math.floor(i / dataPoints * days));
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    if (view === 'failed-volume') {
      current.push({
        date: dateStr,
        incorrectNumber: parseFloat((30 + Math.random() * 20).toFixed(2)) // 2 decimal points
      });
    } else if (view === 'failed') {
      current.push({
        date: dateStr,
        incorrectNumber: Math.floor(Math.random() * 3)
      });
    } else if (view === 'share-of-failures') {
      current.push({
        date: dateStr,
        incorrectNumber: 100
      });
    } else if (view === 'failure-rate') {
      // Previous period uses actual historical date
      const previousDate = new Date(date);
      previousDate.setDate(previousDate.getDate() - days);
      current.push({
        date: date.toISOString().split('T')[0],
        value: parseFloat((1 + Math.random() * 3).toFixed(2))
      });
      previous.push({
        date: previousDate.toISOString().split('T')[0],
        value: parseFloat((1 + Math.random() * 3).toFixed(2))
      });
    }
  }
  return {
    current,
    previous
  };
}